import { useState, useEffect, type FormEvent } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import * as api from "@/lib/api";
import { Button, buttonVariants } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";

export default function CourtTemplateEditPage() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const [name, setName] = useState("");
  const [isDefault, setIsDefault] = useState(false);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    api
      .getCourtTemplate(id)
      .then((res) => {
        setName(res.template.name);
        setIsDefault(res.template.is_default);
      })
      .catch((e) =>
        setError(
          e instanceof api.ApiError
            ? e.message
            : "Failed to load template",
        ),
      )
      .finally(() => setLoading(false));
  }, [id]);

  async function handleSubmit(e: FormEvent) {
    e.preventDefault();
    if (!id) return;
    setError(null);
    setSubmitting(true);

    try {
      await api.updateCourtTemplate(id, { name, is_default: isDefault });
      navigate(`/court-templates/${id}`);
    } catch (err) {
      setError(
        err instanceof api.ApiError
          ? err.message
          : "Failed to update template",
      );
    } finally {
      setSubmitting(false);
    }
  }

  if (loading) {
    return <p className="text-muted-foreground" data-page="court-template-edit" data-state="loading">Loading...</p>;
  }

  return (
    <div className="flex flex-col gap-6" data-page="court-template-edit" data-state={submitting ? "submitting" : error ? "error" : "ready"}>
      <div>
        <h1 className="text-2xl font-semibold">Edit Court Template</h1>
        <p className="text-muted-foreground">
          Update template settings.
        </p>
      </div>

      <Card className="max-w-lg">
        <CardHeader>
          <CardTitle className="text-base">Template Details</CardTitle>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit} className="flex flex-col gap-4" data-form="court-template-edit">
            {error && <p className="text-sm text-destructive" data-error>{error}</p>}

            <div className="flex flex-col gap-2">
              <Label htmlFor="name">Name</Label>
              <Input
                id="name"
                data-field="name"
                placeholder="e.g. Standard Rugby Field"
                value={name}
                onChange={(e) => setName(e.target.value)}
                required
              />
            </div>

            <div className="flex items-center gap-2">
              <input
                id="is-default"
                data-field="is-default"
                type="checkbox"
                checked={isDefault}
                onChange={(e) => setIsDefault(e.target.checked)}
                className="h-4 w-4 rounded border border-input"
              />
              <Label htmlFor="is-default">
                Set as default template for this court type
              </Label>
            </div>

            <div className="flex flex-col gap-2 sm:flex-row">
              <Button type="submit" data-action="submit" disabled={submitting}>
                {submitting ? "Saving..." : "Save Changes"}
              </Button>
              <Link
                to={`/court-templates/${id}`}
                className={buttonVariants({ variant: "outline" })}
                data-action="cancel"
              >
                Cancel
              </Link>
            </div>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
